// ============================================================
// Orymia Shared — Space DSL Types
// SpaceDescriptor, SpaceObject, semantic enums
// The Product Language — describes WHAT the space IS semantically
// ============================================================

import type { SpaceSoundtrack } from "./asset";
import type { ConditionsValue, SpaceMedium, TimeOfDay } from "./atmosphere";
import type { ObjectId, Vector3 } from "./common";
import type { ObjectEffectPresetKey } from "./effectPresets";

// ─── Semantic Terrain ──────────────────────────────────────────────────────

/** Semantic terrain type — describes the ground surface concept, not rendering details.
 *  The Space Compiler maps each value to a TerrainConfig (heightmap, material, size). */
export const SPACE_TERRAINS = [
  "meadow",
  "island",
  "floating",
  "cave",
  "snowfield",
  "desert",
  "mountain",
] as const;
export type SpaceTerrain = (typeof SPACE_TERRAINS)[number];
export const DEFAULT_SPACE_TERRAIN: SpaceTerrain = "meadow";

// ─── Semantic Sky ──────────────────────────────────────────────────────────

/** Semantic sky type — describes the dome/backdrop concept.
 *  The Space Compiler maps each value to a SkyConfig with concrete rendering params. */
export const SPACE_SKIES = [
  "clear",
  "starry",
  "nebula",
  "void",
  "aurora",
] as const;
export type SpaceSky = (typeof SPACE_SKIES)[number];
export const DEFAULT_SPACE_SKY: SpaceSky = "clear";

// ─── Mood & Category ──────────────────────────────────────────────────────

/** Mood tag vocabulary — fixed enum for internal filtering (users never see these) */
export const MOOD_TAGS = [
  "quiet",
  "warm",
  "free",
  "company",
  "inspiration",
  "escape",
] as const;
export type MoodTag = (typeof MOOD_TAGS)[number];

/** Space category — emotional entry point (users choose how they feel) */
export const SPACE_CATEGORIES = ["warm", "quiet", "escape"] as const;
export type SpaceCategory = (typeof SPACE_CATEGORIES)[number];
export const DEFAULT_SPACE_CATEGORY: SpaceCategory = "warm";

/** Listing visibility — stored on the space row, not compiled into the scene. */
export const SPACE_VISIBILITIES = ["public", "private"] as const;
export type SpaceVisibility = (typeof SPACE_VISIBILITIES)[number];
export const DEFAULT_SPACE_VISIBILITY: SpaceVisibility = "public";

export function parseSpaceVisibility(value: unknown): SpaceVisibility {
  return value === "private" ? "private" : DEFAULT_SPACE_VISIBILITY;
}

export function isPrivateVisibility(value: unknown): boolean {
  return parseSpaceVisibility(value) === "private";
}

// ─── Space Objects & Effects ──────────────────────────────────────────────

/** Semantic effect hints on a space object (product language).
 *  Semantic-only: carries a preset KEY, never raw engine config. The compiler
 *  expands the preset into concrete lights/emitters (ObjectEffects) in the
 *  SceneDescriptor. To customize lighting, add a new entry to OBJECT_EFFECT_PRESETS. */
export interface SpaceObjectEffects {
  /** Semantic preset key the compiler expands into concrete effects. e.g. "candle", "campfire". */
  preset?: ObjectEffectPresetKey;
}

/** Optional default camera for a curated space — product-authored viewpoint.
 *  Compiled into SceneDescriptor.cameraHome; overrides auto framing in the client. */
export interface SpaceCameraHome {
  /** OrbitControls look-at point [x, y, z] */
  target: Vector3;
  /** Camera position [x, y, z] */
  position: Vector3;
}

/** Semantic object placement in a space — references an asset by semantic ID.
 *  The Asset Registry resolves the ID to a renderable GLB model.
 *  Describes WHAT exists in the world and WHERE, not HOW to render it. */
export interface SpaceObject {
  /** Semantic asset reference — resolved to GLB via Asset Registry (e.g. "cabin", "giant_tree") */
  asset: ObjectId;
  /** World-space position [x, y, z] */
  position: Vector3;
  /** Uniform scale multiplier: 1 = native size. Default: 1 */
  scale?: number;
  /** Rotation in degrees [pitch, yaw, roll]. Default: [0, 0, 0] */
  rotation?: Vector3;
  /** Semantic effect hints — resolved by the Space Compiler into concrete SceneObject.effects. */
  effects?: SpaceObjectEffects;
}

// ─── Space Descriptor ─────────────────────────────────────────────────────

/** Top-level space entity — the Space DSL (Product Language).
 *  Describes WHAT the space IS semantically — not HOW to render it.
 *  The Space Compiler deterministically maps this to a SceneDescriptor (Engine Language).
 *  Every field must be consumed by the compiler — no speculative fields. */
export interface SpaceDescriptor {
  // -- Identity --
  id: string;
  slug: string;
  name: string;
  nameZh?: string;
  description: string;

  // -- Semantic Core (compiler-consumed) --
  mood: MoodTag[];
  hero?: string; // semantic concept: "giant_tree", "cabin", "crystal" (legacy, prefer objects[])
  /** Ground surface concept. Omit or set to null for space/void/water scenes without terrain. */
  terrain: SpaceTerrain | null;
  sky: SpaceSky;
  /** Atmospheric conditions — composable orthogonal axes (precipitation, cloudCover, wind, visibility).
   *  Accepts preset shorthand string ("storm", "snow", etc.) or full SpaceConditions object.
   *  Replaces the legacy weather: WeatherState field. */
  conditions?: ConditionsValue;
  /** Environment medium — what fills the space. Default: "air".
   *  Explicit source of truth — never auto-inferred from terrain.
   *  Compiler interprets conditions differently per medium (e.g., rain → bubbles underwater). */
  medium?: SpaceMedium;
  timeOfDay: TimeOfDay;

  // -- World Objects (semantic placement) --
  /** Objects placed in the world. Each references an asset by semantic ID. */
  objects?: SpaceObject[];
  /** Default walk-in camera — optional override for curated spaces. */
  cameraHome?: SpaceCameraHome;

  // -- Presentation (product concerns) --
  category: SpaceCategory;
  /** Soundtrack configuration — user-managed. AI assigns initial tracks during generation;
   *  users can change/add/remove via the Space panel. The compiler derives `url` from
   *  `trackId` to produce SoundtrackConfig[] in the SceneDescriptor. */
  soundtracks?: SpaceSoundtrack[];

  // -- Lifecycle --
  createdAt: string;
  updatedAt: string;
  changelog?: SpaceChangeEntry[];
  variationPrompts?: VariationPrompt[];
}

/** Semantic fields the LLM may patch during incremental edits.
 *  Identity/lifecycle fields (id, slug, createdAt, updatedAt, changelog) are server-owned. */
export type SpaceDescriptorPatch = Partial<
  Pick<
    SpaceDescriptor,
    | "name"
    | "nameZh"
    | "description"
    | "mood"
    | "hero"
    | "terrain"
    | "sky"
    | "conditions"
    | "medium"
    | "timeOfDay"
    | "objects"
    | "cameraHome"
    | "soundtracks"
    | "category"
  >
>;

/** LLM response shape for incremental space edits. */
export interface SpaceDescriptorPatchResponse {
  patch: SpaceDescriptorPatch;
}

/** A single entry in the space's change history */
export interface SpaceChangeEntry {
  timestamp: string;
  type: "created" | "ai-generated" | "manual-edit" | "variation" | "curated";
  description: string;
  userId?: string;
}

/** Pre-defined AI variation suggestion for a space */
export interface VariationPrompt {
  label: string;
  prompt: string;
}

// ─── Space Summary (List Projection) ──────────────────────────

/** Lightweight projection of a SpaceDescriptor for list/gallery views.
 *  Omits heavy rendering data (`objects`, `soundtracks`, `changelog`).
 *  `thumbnailUrl` comes from the `spaces.thumbnail_url` column; when absent,
 *  the card derives a palette-based visual placeholder. */
export interface SpaceSummary {
  // -- Identity --
  id: string;
  slug: string;
  name: string;
  nameZh?: string;
  description: string;

  // -- Classification --
  category: SpaceCategory;
  mood: MoodTag[];

  // -- Semantic visuals (for placeholder palettes) --
  sky: SpaceSky;
  timeOfDay: TimeOfDay;
  terrain: SpaceTerrain | null;

  // -- Presentation --
  thumbnailUrl?: string;
  /** Listing visibility. Omitted or "public" on the guest gallery. */
  visibility?: SpaceVisibility;
}

/** Cover URL and listing visibility live on the DB row, not SpaceDescriptor. */
export interface SpacePresentation {
  thumbnailUrl?: string | null;
  visibility?: SpaceVisibility;
}

/** Drop leftover presentation fields so persisted space JSON stays semantic-only. */
export function omitSpacePresentation<T extends object>(space: T): T {
  const { thumbnailUrl: _drop, ...rest } = space as T & {
    thumbnailUrl?: unknown;
  };
  return rest as T;
}

/** Project a full SpaceDescriptor down to a SpaceSummary (strip heavy fields).
 *  Pass `presentation` from the `spaces.thumbnail_url` / visibility columns. */
export function toSpaceSummary(
  space: SpaceDescriptor,
  presentation?: SpacePresentation,
): SpaceSummary {
  const thumbnailUrl = presentation?.thumbnailUrl;
  return {
    id: space.id,
    slug: space.slug,
    name: space.name,
    ...(space.nameZh !== undefined && { nameZh: space.nameZh }),
    description: space.description,
    category: space.category,
    mood: space.mood,
    sky: space.sky,
    timeOfDay: space.timeOfDay,
    terrain: space.terrain,
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(presentation?.visibility !== undefined && {
      visibility: presentation.visibility,
    }),
  };
}
